import { useState, useCallback, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { eightRowerSideViewGeometry } from '../../../config/rowerSideViewGeometry';
import {
  COLORS,
  CAMERA_CONFIG,
  LIGHTING_CONFIG,
  SCENE_HELPERS,
  ANIMATION_CONFIG,
} from './constants';
import { BoatHull } from './BoatHull';
import { WaterSurface } from './WaterSurface';
import { Crew } from './Crew';
import { PlaybackControls, PlaybackState } from './PlaybackControls';
import { strokeDurationToStrokeRate } from './useAnimationClock';
import { AnnotationControls, AnnotationOptionsState } from './AnnotationControls';
import { EnvironmentIndicators, EnvironmentData } from './EnvironmentIndicators';

// Calculate default stroke rate from config
const DEFAULT_STROKE_RATE = strokeDurationToStrokeRate(ANIMATION_CONFIG.STROKE_DURATION);

// Mock environment data (would come from GPS/coach metadata in production)
// Note: boatHeading would update based on GPS data in real implementation
const getMockEnvironmentData = (currentTime: number): EnvironmentData => {
  // Base heading (generally pointing north)
  const baseHeading = 0; // degrees

  // Add realistic variations using multiple sine waves at different frequencies
  // to simulate course corrections and natural drift
  const slowDrift = Math.sin(currentTime * 0.1) * 3; // ±3° slow drift
  const mediumCorrections = Math.sin(currentTime * 0.3) * 2; // ±2° medium corrections
  const microAdjustments = Math.sin(currentTime * 0.7) * 1; // ±1° small adjustments

  // Combine variations for realistic heading
  const heading = baseHeading + slowDrift + mediumCorrections + microAdjustments;

  return {
    wind: {
      speed: 5, // knots
      direction: 45, // degrees (NE)
    },
    current: {
      speed: 2, // knots
      direction: 180, // degrees (flowing South)
    },
    // Realistic boat heading with gradual adjustments
    // In production, this would come from GPS heading data
    boatHeading: heading,
  };
};

/**
 * EightShell - Main Three.js scene component for the 8-person rowing shell visualization
 * Renders a complete animated 3D scene with hull, water, 8 rowers, coxswain, and playback controls
 */
export function EightShell() {
  const geom = eightRowerSideViewGeometry;

  // Playback state
  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: true,
    currentTime: 0,
    playbackSpeed: 1,
    strokeRate: DEFAULT_STROKE_RATE,
    defaultStrokeRate: DEFAULT_STROKE_RATE,
  });

  const [annotationOptions, setAnnotationOptions] = useState<AnnotationOptionsState>({
    showSeatLabels: true,
    showWatts: true,
    showBladeArc: false,
    showPerformanceMetrics: false,
    showWind: true,
    showCurrent: true,
    showCompass: true,
  });

  // Playback control handlers
  const handlePlayPause = useCallback(() => {
    setPlaybackState((prev) => ({ ...prev, isPlaying: !prev.isPlaying }));
  }, []);

  const handleTimeChange = useCallback((time: number) => {
    setPlaybackState((prev) => ({ ...prev, currentTime: time }));
  }, []);

  const handleSpeedChange = useCallback((speed: number) => {
    setPlaybackState((prev) => ({ ...prev, playbackSpeed: speed }));
  }, []);

  const handleStrokeRateChange = useCallback((rate: number) => {
    setPlaybackState((prev) => ({ ...prev, strokeRate: rate }));
  }, []);

  const handleReset = useCallback(() => {
    setPlaybackState({
      isPlaying: true,
      currentTime: 0,
      playbackSpeed: 1,
      strokeRate: DEFAULT_STROKE_RATE,
      defaultStrokeRate: DEFAULT_STROKE_RATE,
    });
  }, []);

  const handleToggleAnnotation = useCallback((key: keyof AnnotationOptionsState) => {
    setAnnotationOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Loop time at max to prevent scrubber from getting stuck
  const maxTime = 60;
  const handleTimeUpdate = useCallback(
    (time: number) => {
      // Loop the time if it exceeds maxTime
      const loopedTime = time % maxTime;
      setPlaybackState((prev) => ({ ...prev, currentTime: loopedTime }));
    },
    [maxTime]
  );

  // Calculate stroke count
  const strokeStats = useMemo(() => {
    const strokeDuration = 60 / playbackState.strokeRate; // seconds per stroke
    // Increment at the end of the drive phase (when oar is at the stern)
    // Offset time forward by recovery phase duration so increment happens after drive completes
    const currentStroke =
      Math.floor(
        (playbackState.currentTime + strokeDuration * ANIMATION_CONFIG.RECOVERY_PHASE_RATIO) /
          strokeDuration
      ) + 1;
    const totalStrokes = Math.floor(maxTime / strokeDuration);
    return { currentStroke, totalStrokes };
  }, [playbackState.currentTime, playbackState.strokeRate, maxTime]);

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
      }}
    >
      {/* Stats Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px 24px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          gap: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Stroke:</span>
          <span style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
            {strokeStats.currentStroke}
          </span>
          <span style={{ fontSize: '14px', color: '#94a3b8' }}>of</span>
          <span style={{ fontSize: '16px', fontWeight: '600', color: '#475569' }}>
            {strokeStats.totalStrokes}
          </span>
        </div>
      </div>

      {/* Playback Controls */}
      <PlaybackControls
        state={playbackState}
        maxTime={maxTime}
        onPlayPause={handlePlayPause}
        onTimeChange={handleTimeChange}
        onSpeedChange={handleSpeedChange}
        onStrokeRateChange={handleStrokeRateChange}
        onReset={handleReset}
      />

      <AnnotationControls options={annotationOptions} onToggle={handleToggleAnnotation} />

      {/* 3D Canvas */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Canvas
          shadows
          dpr={[1, 2]}
          gl={{ antialias: true }}
          camera={{
            position: [0, CAMERA_CONFIG.CAMERA_Y, geom.camera.initialDistance],
            fov: CAMERA_CONFIG.FOV,
            near: CAMERA_CONFIG.NEAR,
            far: CAMERA_CONFIG.FAR,
          }}
          onCreated={({ gl }) => {
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 1.0;
          }}
        >
          <color attach="background" args={[COLORS.BACKGROUND]} />
          <ambientLight intensity={LIGHTING_CONFIG.AMBIENT_INTENSITY} />
          <directionalLight
            position={LIGHTING_CONFIG.DIRECTIONAL_POSITION}
            intensity={LIGHTING_CONFIG.DIRECTIONAL_INTENSITY}
            castShadow
            shadow-mapSize-width={2048}
            shadow-mapSize-height={2048}
            shadow-camera-near={0.5}
            shadow-camera-far={50}
            shadow-camera-left={-20}
            shadow-camera-right={20}
            shadow-camera-top={20}
            shadow-camera-bottom={-20}
          />

          <axesHelper args={[SCENE_HELPERS.AXES_SIZE]} />
          <gridHelper
            args={[
              SCENE_HELPERS.GRID_SIZE,
              SCENE_HELPERS.GRID_DIVISIONS,
              COLORS.GRID_CENTER,
              COLORS.GRID_LINES,
            ]}
          />
          <WaterSurface />
          <BoatHull />
          <Crew
            playbackState={playbackState}
            onTimeUpdate={handleTimeUpdate}
            annotationOptions={annotationOptions}
          />
          <EnvironmentIndicators
            environment={getMockEnvironmentData(playbackState.currentTime)}
            options={{
              showWind: annotationOptions.showWind,
              showCurrent: annotationOptions.showCurrent,
              showCompass: annotationOptions.showCompass,
            }}
          />

          <OrbitControls
            enablePan={false}
            minDistance={CAMERA_CONFIG.MIN_DISTANCE}
            maxDistance={CAMERA_CONFIG.MAX_DISTANCE}
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2.2}
          />
        </Canvas>
      </div>
    </div>
  );
}

export default EightShell;
